import { HttpBase } from '@luminovo/api-contracts';

/** Supplied by the host at startup, so http-client never touches `window` or knows which deployable it's in. */
export interface HttpClientConfig {
    /** No trailing slash: `https://app.luminovo.com` in a browser, `http://localhost:3000` in tests. */
    backendHost: string;

    /** `host:port` of the page, used only as a telemetry attribute. */
    hostName: string;

    /** No trailing slash; `/api` on the same origin. */
    gatewayBase: string;
}

let current: HttpClientConfig | undefined;

/** Call before the first request (not before import); calling again replaces the config, e.g. for tests or reloads. */
export function initHttpClient(config: HttpClientConfig): void {
    assertUsable('backendHost', config.backendHost);
    assertUsable('hostName', config.hostName);
    assertUsable('gatewayBase', config.gatewayBase);

    current = config;
}

export function getHttpClientConfig(): HttpClientConfig {
    if (current === undefined) {
        throw new Error(
            '@luminovo/http-client was used before initHttpClient() ran. Call initHttpClient() ' +
                'from the application entry point or service bootstrap before the first request.',
        );
    }
    return current;
}

export function resolveBase(base: HttpBase = 'api'): string {
    const { backendHost, gatewayBase } = getHttpClientConfig();
    switch (base) {
        case 'api':
            return backendHost + '/api';
        case 'public':
            return backendHost + '/public';
        case 'internal':
            return backendHost + '/int';
        case 'files':
            return backendHost + '/files';
        case 'gateway':
            return gatewayBase;
    }
}

/** The `api` base over `ws`/`wss`; websocket endpoints have no `base` field to choose with. */
export function resolveWebsocketBase(): string {
    return resolveBase('api').replace(/^http/, 'ws');
}

/** Fails now on an unset env var, instead of a request to `undefined/api` much later that says nothing of where. */
function assertUsable(field: keyof HttpClientConfig, value: string): void {
    const complaint = describeProblem(value);
    if (complaint !== undefined) {
        throw new Error(`initHttpClient() was given ${field}=${JSON.stringify(value)}, which is ${complaint}.`);
    }
}

function describeProblem(value: string): string | undefined {
    if (typeof value !== 'string' || value.trim() === '') {
        return 'empty';
    }
    if (value.includes('undefined') || value.includes('null')) {
        return 'the result of interpolating an unset variable';
    }
    if (value.endsWith('/')) {
        return 'trailing a slash — paths are appended with their own leading slash';
    }
    return undefined;
}
